import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Box, Typography } from '@mui/material';
import { OverlayView } from '@react-google-maps/api';
import Map from './Map';
import CloseIcon from '../../images/icon-close.svg';
import { dummyUserImage } from '../../utils/system';

// Stable module-level constants so the controlled `center`/`zoom` props never
// change after mount — that is what previously caused the map to hard-snap
// (teleport) and to reset the user's manual pan on every re-render.
const DEFAULT_CENTER = { lat: 20, lng: 0 };
const DEFAULT_ZOOM = 2;
const FOCUS_ZOOM = 6;
const FIT_BOUNDS_PADDING = 64;

const MemberMarkerWrapper = styled.div`
  position: absolute;
  transform: translate(-20px, -40px);
  cursor: pointer;
  transition: transform 0.2s;
  &:hover {
    transform: translate(-20px, -40px) scale(1.1);
  }
`;

const ProfileImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
`;

const InfoWindowWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  background: #FFF;
  padding: 12px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  min-width: 220px;
  max-width: 280px;
  transform: translate(-50%, calc(-100% - 15px));
  &::before {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top: 10px solid white;
    transform: translateX(-50%);
  }
`;

function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <Box sx={{ display: 'flex', gap: '8px' }}>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', minWidth: 70, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.primary', wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  );
}

function MemberInfoWindow({ member }) {
  const hasDetails = member.department || member.email || member.phone || member.location;
  return (
    <InfoWindowWrapper>
      <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <ProfileImage
          src={member.profilePicture}
          alt={`${member.firstName} ${member.lastName}`}
          onError={(e) => {
            if (e.target.src !== dummyUserImage) {
              e.target.src = dummyUserImage;
            }
          }}
        />
        <Box>
          <Typography variant="subtitle1" fontWeight="600" lineHeight={1.2}>
            {member.firstName}
            {' '}
            {member.lastName}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {member.designation || 'Team Member'}
          </Typography>
        </Box>
      </Box>

      {hasDetails && (
        <Box
          sx={{
            mt: 1.25,
            pt: 1.25,
            borderTop: '1px solid #eee',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          <DetailRow label="Department" value={member.department} />
          <DetailRow label="Email" value={member.email} />
          <DetailRow label="Phone" value={member.phone} />
          <DetailRow label="Location" value={member.location} />
        </Box>
      )}
    </InfoWindowWrapper>
  );
}

function MemberMarker({ member, onSelect, isSelected }) {
  const wrapperRef = useRef(null);

  // Markers render into the map's OVERLAY_MOUSE_TARGET pane, so their DOM events
  // bubble up to the map and fire its onClick — which clears the selection we just
  // made, so the info window never stays open. Stopping only the high-level `click`
  // isn't enough: Google synthesizes the map's click from the low-level
  // mousedown/mouseup/pointer/touch events, so we stop those at the marker too.
  // These are native listeners (not React's, which are delegated above the map and
  // would fire too late to stop Google). Selection still happens on `click`.
  useEffect(() => {
    const node = wrapperRef.current;
    if (!node) return undefined;

    const select = (event) => {
      event.stopPropagation();
      onSelect(member);
    };
    const swallow = (event) => event.stopPropagation();
    const swallowed = [
      'pointerdown', 'pointerup', 'mousedown', 'mouseup', 'touchstart', 'touchend', 'dblclick',
    ];

    node.addEventListener('click', select);
    swallowed.forEach((type) => node.addEventListener(type, swallow));
    return () => {
      node.removeEventListener('click', select);
      swallowed.forEach((type) => node.removeEventListener(type, swallow));
    };
  }, [onSelect, member]);

  return (
    <OverlayView
      position={{ lat: member.lat, lng: member.lng }}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      {/* Selected marker is raised above its neighbours so the open info window
          is never overlapped by adjacent profile images. */}
      {/* The default translate(-20px,-40px) only centers the 40px profile image
          on the map point. When the info window is shown it must NOT inherit that
          shift, or it renders offset from the marker — so anchor the wrapper on the
          exact point and let InfoWindowWrapper position itself above it. */}
      <MemberMarkerWrapper
        ref={wrapperRef}
        style={{
          zIndex: isSelected ? 1000 : 1,
          ...(isSelected ? { transform: 'none' } : null),
        }}
      >
        {isSelected ? (
          <MemberInfoWindow member={member} />
        ) : (
          <ProfileImage
            src={member.profilePicture}
            alt={`${member.firstName} ${member.lastName}`}
            onError={(e) => {
              if (e.target.src !== dummyUserImage) {
                e.target.src = dummyUserImage;
              }
            }}
          />
        )}
      </MemberMarkerWrapper>
    </OverlayView>
  );
}

const CloseBtn = styled.div`
  background: white;
  filter: drop-shadow(0px 0.864px 3.458px rgba(0, 0, 0, 0.25));
  border-radius: 100px;
  padding: 10px;
  position: absolute;
  right: 33px;
  top: 33px;
  cursor: pointer;
`;

function MembersMap({ members, handleClose }) {
  const mapRef = useRef(null);
  const [selectedMember, setSelectedMember] = useState(null);

  // Frame the map around the loaded members. Runs on map load and whenever the
  // member set changes (data arrives asynchronously, so this is what actually
  // centers the map — a one-time useState initializer could not).
  const fitToMembers = useCallback(() => {
    const map = mapRef.current;
    if (!map || members.length === 0) return;

    if (members.length === 1) {
      map.setCenter({ lat: members[0].lat, lng: members[0].lng });
      map.setZoom(FOCUS_ZOOM);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    members.forEach((member) => bounds.extend({ lat: member.lat, lng: member.lng }));
    map.fitBounds(bounds, FIT_BOUNDS_PADDING);
  }, [members]);

  const handleMapLoad = useCallback((map) => {
    mapRef.current = map;
    fitToMembers();
  }, [fitToMembers]);

  useEffect(() => {
    fitToMembers();
  }, [fitToMembers]);

  // Stable so MemberMarker's native click listener isn't torn down and
  // re-attached on every selection change.
  const handleMarkerClick = useCallback((member) => {
    setSelectedMember(member.id);
    const map = mapRef.current;
    if (!map) return;
    // Smooth animated pan instead of re-centering via a controlled prop.
    map.panTo({ lat: member.lat, lng: member.lng });
    if (map.getZoom() < FOCUS_ZOOM) {
      map.setZoom(FOCUS_ZOOM);
    }
  }, []);

  return (
    <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
      <Map
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        onLoad={handleMapLoad}
        onClick={() => setSelectedMember(null)}
      >
        {members.map((member) => (
          <MemberMarker
            key={`member-${member.id}`}
            member={member}
            onSelect={handleMarkerClick}
            isSelected={selectedMember === member.id}
          />
        ))}
        <CloseBtn onClick={handleClose} data-testid="close-btn">
          <img src={CloseIcon} alt="close icon" />
        </CloseBtn>
      </Map>
    </Box>
  );
}

MembersMap.propTypes = {
  members: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      lat: PropTypes.number.isRequired,
      lng: PropTypes.number.isRequired,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      profilePicture: PropTypes.string.isRequired,
      designation: PropTypes.string,
      department: PropTypes.string,
      email: PropTypes.string,
      phone: PropTypes.string,
      location: PropTypes.string,
    }),
  ).isRequired,
  handleClose: PropTypes.func.isRequired,
};

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};

DetailRow.defaultProps = {
  value: '',
};

MemberInfoWindow.propTypes = {
  member: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    profilePicture: PropTypes.string.isRequired,
    designation: PropTypes.string,
    department: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    location: PropTypes.string,
  }).isRequired,
};

MemberMarker.propTypes = {
  member: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    lat: PropTypes.number.isRequired,
    lng: PropTypes.number.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    profilePicture: PropTypes.string.isRequired,
    designation: PropTypes.string,
    department: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    location: PropTypes.string,
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
};

export default MembersMap;
